import { createSelector } from 'reselect';
import { selectUserMassJobMappingConfig } from 'redux/user/selectors';
import { GLOBAL_LEVEL_NOT_APPLICABLE } from 'common/constants';

export function selectCompanyJobsFilter(state) {
  return state.mapping.job.company.filter;
}

export function selectCreateCompanyJobs(state) {
  return state.mapping.job.company.createdCompanyJobs;
}

function selectCompanyJobFamilies(state) {
  return state.mapping.job.company.jobFamilies;
}

export const selectCompanyJobFamilyOptions = createSelector(
  selectCompanyJobFamilies,
  (jobFamilies) => jobFamilies.map(
    family => ({
      value: `family-${family.id}`,
      label: family.familyTitle,
      options: family.subFamilies.map(
        ({ id, title }) => ({ value: id, label: title })
      ),
    })
  ),
);

function selectCompanyJobGrades(state) {
  return state.mapping.job.company.grades;
}

export const selectCompanyJobGradesOptions = createSelector(
  selectCompanyJobGrades,
  (grades) => grades.map(({ grade }) => ({ value: grade, label: grade })),
);

function selectCompanyJobTags(state) {
  return state.mapping.job.company.tags;
}

export const selectCompanyJobTagOptions = createSelector(
  selectCompanyJobTags,
  (tags) => tags.map(({ id, name, emoji }) => ({ value: id, label: `${emoji ? `${emoji}  ` : ''}${name}` })),
);

export function selectCompanyPaymarkets(state) {
  return state.mapping.job.company.paymarkets;
}

export function selectCompanyPlans(state) {
  return state.mapping.job.company.plans;
}

export const selectCompanyPlanOptions = createSelector(
  selectCompanyPlans,
  (plans) => plans.map(({ id, name }) => ({ value: id, label: name }))
);
function selectCompanyJobLevels(state) {
  return state.mapping.job.company.jobLevels;
}

export const selectCompanyJobLevelOptions = createSelector(
  selectCompanyJobLevels,
  (jobLevels) => jobLevels.map(({ id, levelCode }) => ({ value: id, label: levelCode }))
);

export const selectSurveyJobProfiles = (state) => {
  return state.mapping.job.survey.surveyJobProfiles;
};

export const selectSurveyJobProfileOptions = createSelector(
  selectSurveyJobProfiles,
  (jobProfiles) => jobProfiles.map(({ id, name }) => ({ value: id, label: name }))
);

export const selectedSortedSurveyJobProfileOptions = createSelector(
  selectSurveyJobProfiles,
  selectUserMassJobMappingConfig,
  (surveyJobProfiles, massJobMappingConfig) => {
    return [...surveyJobProfiles].sort((a, b) => {
      const positionA = massJobMappingConfig.indexOf(a.id);
      const positionB = massJobMappingConfig.indexOf(b.id);
      if (positionA === -1) {
        return 1;
      }
      if (positionB === -1) {
        return -1;
      }
      return positionA > positionB ? 1 : -1;
    }).map(({ id, name }) => ({ value: id, label: name }))
  }
);

export function selectActiveSurveyJobProfileId(state) {
  return state.mapping.job.survey.activeSurveyJobProfileId;
}

export const selectSurveyModules = (state) => {
  return state.mapping.job.survey.surveyModules;
};

export const selectSurveyModuleOptions = createSelector(
  selectSurveyModules,
  (surveyModules) => surveyModules.map(({ id, title }) => ({ value: id, label: title }))
);

export function selectActiveSurveyModuleId(state) {
  return state.mapping.job.survey.activeSurveyModuleId;
}

export function selectSurveyJobsFilter(state) {
  return state.mapping.job.survey.filter;
}

export const selectSurveyJobDefinitions = (state) => {
  return state.mapping.job.survey.jobs;
};

export const selectSurveyJobDefinitionsLoading = (state) => {
  return state.mapping.job.survey.isSurveyJobsLoading;
};

function selectSurveyJobFamilies(state) {
  return state.mapping.job.survey.jobFamilies;
}

export const selectSurveyJobFamilyOptions = createSelector(
  selectSurveyJobFamilies,
  (jobFamilies) => jobFamilies.map(
    family => ({
      label: family.familyTitle,
      value: family.id,
      options: family.SurveyJobSubFamilies.map(
        (subFamily) => ({
          value: subFamily.id,
          label: subFamily.subFamilyTitle,
          options: subFamily.SurveyJobFocus.map(focus => ({ value: focus.id, label: focus.focusTitle })),
        })
      ),
    })
  ),
);

function selectCareerLevels(state) {
  return state.mapping.job.survey.careerLevels;
}

function selectGlobalLevels(state) {
  return state.mapping.job.survey.globalLevels;
}

export function selectMappedLevels(state) {
  return state.mapping.job.survey.mappedLevels;
}

export function selectMappedLevelsGlobalGrade(state) {
  return state.mapping.job.survey.mappedLevelsGlobalGrade;
}

export const selectCareerLevelOptions = createSelector(
  selectCareerLevels,
  selectMappedLevels,
  (jobLevels, mappedLevelIds) => {
    const preMappedLevels = jobLevels
      .filter(level => mappedLevelIds.includes(level.id))
      .map(({ id, levelTitle, levelCode }) => ({ value: id, label: `${levelCode} ${levelTitle}` }));

    const unmappedOptions = jobLevels
      .filter(level => !mappedLevelIds.includes(level.id))
      .map(({ id, levelTitle, levelCode }) => ({ value: id, label: `${levelCode} ${levelTitle}` }));

    return [
      ...(preMappedLevels.length ? [{ label: 'Pre-Mapped Levels', options: preMappedLevels, value: 'pre-mapped' }] : []),
      ...unmappedOptions,
    ];
  }
);

export const selectGlobalLevelOptions = createSelector(
  selectGlobalLevels,
  selectMappedLevelsGlobalGrade,
  (jobLevels, mappedLevelIds) => {

    const preMappedLevels = jobLevels
      .filter(level => mappedLevelIds.includes(level.id))
      .map(({ id, levelTitle, levelCode }) => ({ value: id, label: `${levelCode} ${levelTitle}` }));

    const unmappedOptions = jobLevels
      .filter(level => !mappedLevelIds.includes(level.id))
      .map(({ id, levelTitle, levelCode }) => ({ value: id, label: `${levelCode} ${levelTitle}` }));

    return [
      { label: 'Not Applicable', value: GLOBAL_LEVEL_NOT_APPLICABLE },
      ...(preMappedLevels.length ? [{ label: 'Pre-Mapped Levels', options: preMappedLevels, value: 'pre-mapped' }] : []),
      ...unmappedOptions,
    ];
  }
);

const selectJobFamilyOptions = createSelector(
  selectSurveyJobFamilies,
  (jobFamilies) => jobFamilies.map(
    family => ({
      label: family.familyTitle,
      value: family.id,
    })
  ),
);

const selectJobSubFamilyOptions = createSelector(
  selectSurveyJobFamilies,
  (jobFamilies) => jobFamilies.reduce((res, family) => {
    return [...res, ...family.SurveyJobSubFamilies.map(
      (subFamily) => ({
        value: subFamily.id,
        label: subFamily.subFamilyTitle,
      })
    )];
  }, [])
);

const selectJobFocusOptions = createSelector(
  selectSurveyJobFamilies,
  (jobFamilies) => jobFamilies.reduce((res, family) => {
    return [...res, ...family.SurveyJobSubFamilies.reduce((subRes, subFamily)=> {
      return [...subRes, ...subFamily.SurveyJobFocus.map(focus => ({ value: focus.id, label: focus.focusTitle }))];
    }, [])];
  }, [])
);

export const selectAddJobMappingFilterOptions = createSelector(
  selectJobFamilyOptions,
  selectJobSubFamilyOptions,
  selectJobFocusOptions,
  selectCareerLevelOptions,
  selectGlobalLevelOptions,
  selectSurveyModuleOptions,
  (jobFamily, jobSubFamily, jobFocus, careerLevel, globalLevel, module) => ({
    jobFamily,
    jobSubFamily,
    jobFocus,
    careerLevel,
    globalLevel,
    module,
  })
);
